/**
 * EditParcelaManualModal — edição de PARCELA individual em cobranças de conta
 * corrente SEM integração (controle manual).
 *
 * Campos: vencimento, valor e descrição/observação da parcela. O total da
 * cobrança-pai é recalculado no banco como soma das parcelas ativas.
 *
 * @version 1.0.1
 * RELEASE: 2026-09-12 v1.0.1 - Campo "Valor da parcela" com máscara de moeda BRL.
 * RELEASE: 2026-09-12 v1.0.0 - Versão inicial.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Loader2, Pencil } from 'lucide-react';
import { useUpdateParcelaManual } from '@/hooks/useUpdateParcelaManual';
import {
  applyCurrencyInputMask,
  formatCurrency,
  parseCurrencyValue,
} from '@/lib/currency-utils';

export interface ParcelaManualAlvo {
  id: string;
  numero_parcela: number;
  valor: number;
  data_vencimento: string;
  metadata?: Record<string, unknown> | null;
}

interface EditParcelaManualModalProps {
  parcela: ParcelaManualAlvo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditParcelaManualModal({
  parcela,
  open,
  onOpenChange,
}: EditParcelaManualModalProps) {
  const { updateParcela, isSaving } = useUpdateParcelaManual();

  const [dataVencimento, setDataVencimento] = useState('');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');

  // Hidrata o formulário sempre que uma parcela é aberta
  useEffect(() => {
    if (!open || !parcela) return;
    setDataVencimento(parcela.data_vencimento || '');
    setValor(formatCurrency(Number(parcela.valor ?? 0), { currency: 'BRL' }));
    setDescricao(((parcela.metadata?.descricao as string) ?? ''));
  }, [open, parcela]);

  const handleSave = async () => {
    if (!parcela) return;
    const valorNum = parseCurrencyValue(valor, 'pt-BR');
    if (!Number.isFinite(valorNum) || valorNum <= 0) return;

    await updateParcela({
      parcela_id: parcela.id,
      acao: 'update',
      data_vencimento: dataVencimento || null,
      valor: valorNum,
      descricao: descricao,
    });
    onOpenChange(false);
  };

  if (!parcela) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-sm:fixed max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Pencil className="h-4 w-4 text-primary" />
            Editar Parcela #{parcela.numero_parcela}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Cobrança sem integração — a alteração vale apenas nesta parcela.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <InlineNotice variant="info" className="text-xs">
            O valor total da cobrança passa a ser a soma das parcelas ativas.
          </InlineNotice>

          <div>
            <Label className="text-xs mb-1 block">Data de Vencimento</Label>
            <Input
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs mb-1 block">Valor da parcela</Label>
            <Input
              type="text"
              inputMode="decimal"
              placeholder={formatCurrency(0, { currency: 'BRL' })}
              value={valor}
              onChange={(e) => setValor(applyCurrencyInputMask(e.target.value, { currency: 'BRL' }))}
            />
          </div>

          <div>
            <Label className="text-xs mb-1 block">Descrição / observação</Label>
            <Textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Anotação desta parcela (uso interno)"
              rows={3}
              maxLength={500}
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
